(() => {
  const mobileQuery = window.matchMedia("(max-width: 699px) and (hover: none) and (pointer: coarse)");
  const widths = [200, 300, 400, 500, 600, 700, 800, 1000, 1200, 1400, 1600, 1800];

  const withWidth = (src, width) => {
    if (/[?&]width=\d+/.test(src)) return src.replace(/([?&]width=)\d+/, `$1${width}`);
    return `${src}${src.includes("?") ? "&" : "?"}width=${width}`;
  };

  const isGridCard = (card) => {
    if (card.closest(".product-grid-block, [data-product-grid-items]")) return true;
    const list = card.closest(".product-list");
    if (!list) return false;
    return !list.classList.contains("product-list--carousel") || list.dataset.mobileLayout === "grid";
  };

  const fileOf = (url) => (url || "").split("?")[0].split("/").pop();

  const parseSlides = (raw) => {
    if (!raw) return null;
    try {
      const data = JSON.parse(raw);
      return data && Array.isArray(data.groups) ? data : null;
    } catch (error) {
      return null;
    }
  };

  const readData = (card) => {
    if (card._variantSlides) return card._variantSlides;
    const sharedId = card.getAttribute("data-variant-slides-ref");
    const shared = sharedId ? document.getElementById(sharedId) : null;
    if (shared) {
      const data = shared._variantSlides || parseSlides(shared.textContent);
      if (data) shared._variantSlides = card._variantSlides = data;
      return data;
    }
    const data = parseSlides(card.getAttribute("data-variant-slides"))
      || parseSlides(card.querySelector(':scope > script[type="application/json"][data-variant-slides]')?.textContent);
    if (data) card._variantSlides = data;
    return data;
  };

  const groupFor = (card, data) => {
    const primary = card.querySelector(".product-card__image--primary");
    const file = primary ? fileOf(primary.currentSrc || primary.getAttribute("src") || "").toLowerCase() : "";
    if (!file) return null;
    return data.groups.find((group) => group.images.some((image) => fileOf(image.src).toLowerCase() === file)) || null;
  };

  const removeScrollers = (root = document) => {
    root.querySelectorAll(".product-card__variant-scroller, .product-card__variant-pager").forEach((node) => node.remove());
  };

  const guarded = new WeakSet();
  const bindGuard = (link, scroller) => {
    if (guarded.has(scroller)) return;
    guarded.add(scroller);
    let startX = 0;
    let startScroll = 0;
    let blockClick = false;
    const arm = () => {
      if (Math.abs(scroller.scrollLeft - startScroll) > 4) blockClick = true;
    };
    scroller.addEventListener("pointerdown", (event) => {
      startX = event.clientX;
      startScroll = scroller.scrollLeft;
      blockClick = false;
    }, { passive: true });
    scroller.addEventListener("pointerup", (event) => {
      if (Math.abs(event.clientX - startX) > 8) blockClick = true;
      arm();
    }, { passive: true });
    scroller.addEventListener("pointercancel", () => {
      arm();
    }, { passive: true });
    scroller.addEventListener("scroll", arm, { passive: true });
    link.addEventListener("click", (event) => {
      if (!blockClick) return;
      event.preventDefault();
      event.stopPropagation();
      blockClick = false;
    }, true);
  };

  const paged = new WeakSet();
  const syncPager = (rail, pager) => {
    const slides = rail.querySelectorAll(".product-card__variant-slide");
    if (!slides.length) return;
    const index = Math.max(0, Math.min(slides.length - 1, Math.round(rail.scrollLeft / Math.max(1, rail.clientWidth))));
    pager.querySelectorAll("span").forEach((dot, i) => {
      dot.classList.toggle("is-active", i === index);
    });
  };
  const bindPager = (rail) => {
    if (paged.has(rail)) return;
    paged.add(rail);
    rail.addEventListener("scroll", () => {
      const pager = rail.parentElement?.querySelector(":scope > .product-card__variant-pager");
      if (pager) syncPager(rail, pager);
    }, { passive: true });
  };

  const syncCard = (card) => {
    if (!(card instanceof HTMLElement)) return;
    const scroller = card.querySelector(".product-card__variant-scroller");
    const pager = card.querySelector(".product-card__variant-pager");
    if (!mobileQuery.matches || card.classList.contains("b4b-hidden") || card.style.display === "none" || !isGridCard(card)) {
      scroller?.remove();
      pager?.remove();
      return;
    }
    const data = readData(card);
    if (!data) {
      scroller?.remove();
      pager?.remove();
      return;
    }
    const group = groupFor(card, data);
    const images = group && group.images.length > 1 ? group.images : null;
    const link = card.querySelector("a.product-card__media");
    if (!images || !link) {
      scroller?.remove();
      pager?.remove();
      return;
    }
    const signature = `${group.id}:${images.map((image) => image.src).join("|")}`;
    let rail = link.querySelector(":scope > .product-card__variant-scroller");
    if (rail && rail.dataset.signature === signature) return;
    if (!rail) {
      rail = document.createElement("div");
      rail.className = "product-card__variant-scroller";
      link.append(rail);
    }
    const primary = link.querySelector(".product-card__image--primary");
    rail.replaceChildren(...images.map((image, index) => {
      const img = document.createElement("img");
      img.className = "product-card__variant-slide b4b-ignore";
      img.alt = image.alt || primary?.alt || "";
      img.draggable = false;
      img.decoding = "async";
      img.loading = index === 0 ? primary?.loading || "eager" : "lazy";
      img.sizes = primary?.sizes || "(max-width: 699px) 50vw, 300px";
      img.src = image.src;
      img.srcset = widths.map((width) => `${withWidth(image.src, width)} ${width}w`).join(", ");
      if (image.w) img.width = image.w;
      if (image.h) img.height = image.h;
      return img;
    }));
    rail.dataset.signature = signature;
    rail.scrollLeft = 0;
    rail.classList.add("is-ready");
    bindGuard(link, rail);

    let ticks = link.querySelector(":scope > .product-card__variant-pager");
    if (!ticks) {
      ticks = document.createElement("div");
      ticks.className = "product-card__variant-pager";
      link.append(ticks);
    }
    ticks.replaceChildren(...images.map((_, index) => {
      const mark = document.createElement("span");
      if (index === 0) mark.className = "is-active";
      return mark;
    }));
    bindPager(rail);
    syncPager(rail, ticks);
  };

  let queued = false;
  let observer = null;
  const scan = () => {
    queued = false;
    if (!mobileQuery.matches) {
      removeScrollers();
      return;
    }
    document.querySelectorAll("product-card").forEach(syncCard);
  };
  const scheduleScan = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(scan);
  };

  const enableObserver = () => {
    if (observer) return;
    observer = new MutationObserver(scheduleScan);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["href", "data-variant-id"] });
  };

  const disableObserver = () => {
    observer?.disconnect();
    observer = null;
    removeScrollers();
  };

  const onBreakpoint = () => {
    if (mobileQuery.matches) {
      enableObserver();
      scheduleScan();
      return;
    }
    disableObserver();
  };

  const start = () => {
    mobileQuery.addEventListener("change", onBreakpoint);
    document.addEventListener("shopify:section:load", scheduleScan);
    onBreakpoint();
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
})();
